import React from "react";
import { useParams, Link } from "react-router-dom";
import { useQuery } from "@tanstack/react-query";
import axios from "axios";
import { motion } from "framer-motion";
import { ArrowLeft, ShoppingBag, ShieldCheck, Truck, Award } from "lucide-react";
import { toast } from "sonner";
import { useCart } from "../context/CartContext";
import { ArtworkCard } from "../components/ArtworkCard";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

export default function ArtworkDetailPage() {
  const { id } = useParams();
  const { addItem } = useCart();

  const { data: artwork, isLoading } = useQuery({
    queryKey: ["artwork", id],
    queryFn: async () => (await axios.get(`${API}/artworks/${id}`)).data,
  });

  const { data: all } = useQuery({
    queryKey: ["artworks"],
    queryFn: async () => (await axios.get(`${API}/artworks`)).data.artworks,
  });

  if (isLoading || !artwork) {
    return (
      <div className="pt-32 px-6 sm:px-12 lg:px-20 min-h-screen" data-testid="artwork-loading">
        <div className="aspect-[4/3] max-w-3xl bg-[#FBF3E2] animate-pulse border border-[rgba(42,33,24,0.06)]" />
      </div>
    );
  }

  const isOriginal = artwork.type === "original";
  const related = (all || []).filter((a) => a.id !== artwork.id && a.type === artwork.type).slice(0, 3);

  return (
    <div className="pt-28 pb-24 px-6 sm:px-12 lg:px-20 min-h-screen" data-testid="artwork-detail-page">
      <Link
        to="/shop"
        data-testid="artwork-back-link"
        className="inline-flex items-center gap-2 font-mono-accent text-[11px] uppercase tracking-[0.3em] text-[#7A6C5A] hover:text-[#2A2118] transition-colors mb-10"
      >
        <ArrowLeft size={14} /> Back to collection
      </Link>

      <div className="grid grid-cols-1 lg:grid-cols-12 gap-12 lg:gap-20">
        <motion.div
          initial={{ opacity: 0, scale: 0.97 }}
          animate={{ opacity: 1, scale: 1 }}
          transition={{ duration: 0.9, ease: [0.22, 1, 0.36, 1] }}
          className="lg:col-span-7"
        >
          <div className="relative overflow-hidden border border-[rgba(42,33,24,0.12)] bg-[#FBF3E2]" data-testid="artwork-image-frame">
            <img
              src={artwork.image_url}
              alt={artwork.title}
              className="w-full h-auto object-cover"
              data-testid="artwork-image"
            />
            {!artwork.is_available && (
              <div className="absolute inset-0 bg-[#F5E8D0]/55 flex items-center justify-center">
                <span className="font-mono-accent text-xs uppercase tracking-[0.35em] text-[#2A2118] border border-[#2A2118]/40 px-6 py-3">
                  Sold
                </span>
              </div>
            )}
          </div>
        </motion.div>

        <motion.div
          initial={{ opacity: 0, y: 30 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.9, delay: 0.15, ease: [0.22, 1, 0.36, 1] }}
          className="lg:col-span-5"
        >
          <span
            className={`font-mono-accent text-[10px] uppercase tracking-[0.25em] px-3 py-1.5 border ${
              isOriginal ? "text-[#C9501C] border-[#C9501C]/30" : "text-[#B8860B] border-[#B8860B]/40"
            }`}
            data-testid="artwork-type-badge"
          >
            {isOriginal ? "Original · 1 of 1" : "Archival Print"}
          </span>

          <h1
            className="mt-6 font-serif-display text-4xl sm:text-5xl tracking-tight leading-tight text-[#2A2118]"
            data-testid="artwork-title"
          >
            {artwork.title}
          </h1>

          <p className="mt-5 text-base leading-relaxed text-[#7A6C5A]" data-testid="artwork-description">
            {artwork.description}
          </p>

          <dl className="mt-8 space-y-4 border-y border-[rgba(42,33,24,0.08)] py-7">
            {[
              ["Medium", artwork.medium],
              ["Dimensions", artwork.dimensions],
              ["Year", artwork.year],
              ...(artwork.edition ? [["Edition", artwork.edition]] : []),
            ].map(([k, v]) => (
              <div key={k} className="flex items-baseline justify-between gap-6">
                <dt className="font-mono-accent text-[10px] uppercase tracking-[0.25em] text-[#AB9C86]">
                  {k}
                </dt>
                <dd className="text-sm text-[#2A2118] text-right">{v}</dd>
              </div>
            ))}
          </dl>

          <div className="mt-7 flex items-baseline justify-between">
            <span className="font-mono-accent text-[10px] uppercase tracking-[0.3em] text-[#AB9C86]">
              Price
            </span>
            <span className="font-mono-accent text-3xl font-semibold text-[#2A2118]" data-testid="artwork-price">
              ${artwork.price.toLocaleString()}
            </span>
          </div>

          <button
            data-testid="add-to-cart-button"
            disabled={!artwork.is_available}
            onClick={() => {
              addItem(artwork);
              toast.success(`"${artwork.title}" added to your cart.`);
            }}
            className="mt-7 w-full flex items-center justify-center gap-3 bg-[#C9501C] hover:bg-[#f02a4a] disabled:bg-[#E4D9C0] disabled:text-[#AB9C86] disabled:cursor-not-allowed text-[#2A2118] font-mono-accent text-xs uppercase tracking-[0.3em] py-5 transition-colors duration-300"
          >
            <ShoppingBag size={15} />
            {artwork.is_available ? "Add to cart" : "This piece has sold"}
          </button>

          <div className="mt-7 space-y-3">
            {[
              [Truck, "Insured worldwide shipping included"],
              [ShieldCheck, "Secure checkout via Stripe"],
              [Award, "Signed certificate of authenticity"],
            ].map(([Icon, text]) => (
              <div key={text} className="flex items-center gap-3 text-xs text-[#7A6C5A]">
                <Icon size={14} className="text-[#C9501C]" />
                <span>{text}</span>
              </div>
            ))}
          </div>
        </motion.div>
      </div>

      {related.length > 0 && (
        <section className="mt-28" data-testid="related-works-section">
          <h2 className="font-serif-display text-2xl sm:text-3xl text-[#2A2118] tracking-tight mb-10">
            More {isOriginal ? "originals" : "prints"}
          </h2>
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-8 lg:gap-12">
            {related.map((art, i) => (
              <ArtworkCard key={art.id} artwork={art} index={i} />
            ))}
          </div>
        </section>
      )}
    </div>
  );
}
